
import { AssessmentResult } from '@/types/assessment';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { isValidUUID } from '../validation/validationUtils';
import { createClient } from '@supabase/supabase-js';

// Regular supabase client for most operations
const regularClient = supabase;

// Special admin client that bypasses RLS for specific operations
// This uses the service role key which has admin privileges
// IMPORTANT: This should ONLY be used for operations that need to bypass RLS
const SUPABASE_URL = "https://obuhrpfphcqeqpvttcum.supabase.co";
const SUPABASE_SERVICE_ROLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9idWhycGZwaGNxZXFwdnR0Y3VtIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0MTQxNDAxMSwiZXhwIjoyMDU2OTkwMDExfQ.39NFN8WM_z52JXHh--qRs6m_viIW5K1VuQTr0iVmYTU";

// Create an admin client that bypasses RLS policies
// WARNING: This should be used ONLY for operations that need admin access
const adminClient = createClient(
  SUPABASE_URL,
  SUPABASE_SERVICE_ROLE_KEY
);

/**
 * Saves a report to local storage as a backup
 */
export const saveReportToLocalStorage = (userId: string, result: AssessmentResult): void => {
  try {
    localStorage.setItem(`report_${userId}`, JSON.stringify(result));
  } catch (e) {
    // Silent fail
  }
};

/**
 * Saves a report to the Supabase database
 */
export const saveReportToDatabase = async (
  userId: string, 
  report: string, 
  pdfUrl: string = '',
  orderId?: string
): Promise<{id: string} | null> => {
  try {
    if (!isValidUUID(userId)) {
      return null;
    }

    // Create data object with types that match the database schema
    const reportData = {
      user_id: userId,
      report: report,
      pdf_url: pdfUrl || null,
      order_id: orderId || null
    };
    
    // Cast to any to bypass TypeScript errors since we're validating at runtime
    const { data: reportData1, error: reportError } = await supabase
      .from('assessment_results')
      .insert(reportData as any)
      .select()
      .single();
        
    if (reportError) {
      toast.error(`Failed to save report to database. Your report is available temporarily.`);
      return null;
    } 
    
    // Cast the data to include id 
    const returnData = reportData1 as unknown as {id: string};
    toast.success('Report saved successfully!');
    return { id: returnData.id };
  } catch (e) {
    toast.error('Error saving report to database. Your report is available temporarily.');
    return null;
  }
};

/**
 * Updates the PDF URL for an existing assessment result
 * This function is now a facade that calls the Edge Function
 */
export const updatePdfUrl = async (resultId: string, pdfUrl: string): Promise<boolean> => {
  try {
    if (!isValidUUID(resultId)) {
      return false;
    }
    
    if (!pdfUrl || typeof pdfUrl !== 'string') {
      return false;
    }
    
    try {
      // Get current session token for authorization
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || '';
      
      // Try using the Edge Function first (preferred method)
      const response = await fetch(`https://obuhrpfphcqeqpvttcum.supabase.co/functions/v1/update-pdf-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          resultId,
          pdfUrl
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        return true;
      }
    } catch (error) {
      // Silent catch, will fall back to direct update
    }
    
    // Fallback to direct database update with admin client
    
    // First, check if the row exists using the regular client
    const { data: existingResult, error: checkError } = await regularClient
      .from('assessment_results')
      .select('id')
      .eq('id', resultId)
      .maybeSingle();
      
    if (checkError || !existingResult) {
      return false;
    }
    
    // Use the admin client with service role key to bypass RLS policies
    // This ensures the update works even if the user doesn't have direct update permissions
    const { error } = await adminClient
      .from('assessment_results')
      .update({ pdf_url: pdfUrl })
      .eq('id', resultId)
      .select();
      
    return !error;
  } catch (e) {
    return false;
  }
};

/**
 * Saves responses to local storage as a backup
 */
export const saveResponsesToLocalStorage = (userId: string, responses: any[]): void => {
  try {
    localStorage.setItem(`responses_${userId}`, JSON.stringify(responses));
  } catch (e) {
    // Silent fail
  }
};
